import React, { useEffect, useState } from 'react';
import Portal from './Portal';
import { useTracking } from '../hooks/useTracking';
import { isEuCountry } from '../utils/euCountries';

/**
 * 18+ age gate shown only to EU/EEA/UK visitors.
 *
 * Detection: on mount we fetch `/geo` (served by server.js, which reads
 * Cloudflare's CF-IPCountry header). If the country is in the EU/EEA/UK set
 * AND the visitor hasn't already confirmed, we show a blocking overlay.
 *
 * Persistence: a confirmed "18+" is stored in localStorage so the gate only
 * appears once per browser. Choosing "under 18" redirects the visitor away.
 *
 * Testing: append `?forceAgeGate=1` to any URL to force the EU path from a
 * non-EU IP (bypasses the /geo check, still respects the stored confirmation).
 */

// Where under-18 visitors are sent.
const EXIT_URL = 'https://www.google.com';

// localStorage flag: set to '1' once the visitor confirms they are 18+.
const AGE_VERIFIED_KEY = 'ga:ageVerified';

function isAgeVerified(): boolean {
  try {
    return localStorage.getItem(AGE_VERIFIED_KEY) === '1';
  } catch {
    return false;
  }
}

function setAgeVerified(): void {
  try {
    localStorage.setItem(AGE_VERIFIED_KEY, '1');
  } catch {
    // Private browsing / storage disabled — non-fatal; gate may re-show.
  }
}

export default function AgeGateOverlay() {
  const { track } = useTracking();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Already confirmed on this browser — never show again.
    if (isAgeVerified()) return;

    const forced =
      new URLSearchParams(window.location.search).get('forceAgeGate') === '1';

    let cancelled = false;

    const decide = async () => {
      if (forced) {
        if (!cancelled) setVisible(true);
        return;
      }

      try {
        const res = await fetch('/geo', { headers: { Accept: 'application/json' } });
        if (!res.ok) return; // Fail open: don't block if geo lookup fails.
        const { country } = (await res.json()) as { country?: string };
        if (!cancelled && isEuCountry(country)) {
          setVisible(true);
        }
      } catch {
        // Network error — fail open (do not block the site).
      }
    };

    decide();
    return () => {
      cancelled = true;
    };
  }, []);

  // Lock body scroll while the gate is up.
  useEffect(() => {
    if (!visible) return;
    track('age_gate_shown', { path: window.location.pathname });
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible, track]);

  if (!visible) return null;

  const handleConfirmAdult = () => {
    track('age_gate_confirmed_adult', { path: window.location.pathname });
    setAgeVerified();
    setVisible(false);
  };

  const handleRejectMinor = () => {
    track('age_gate_rejected_minor', { path: window.location.pathname });
    window.location.href = EXIT_URL;
  };

  return (
    <Portal>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-gate-title"
        className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
      >
        <div className="bg-bg border border-white/20 rounded-xl p-6 sm:p-8 w-full max-w-md shadow-lg text-center">
          <h2 id="age-gate-title" className="text-2xl font-bold text-white mb-4">
            Are you 18 or older?
          </h2>
          <p className="text-white/80 text-base mb-8">
            You must be at least 18 years old to enter this site.
          </p>

          <div className="space-y-3">
            <button
              onClick={handleConfirmAdult}
              className="w-full bg-accent text-white btn-glow px-6 py-3 min-h-[48px] rounded-full text-base font-semibold hover:bg-accent-hover transition-colors"
            >
              I'm 18 or older
            </button>
            <button
              onClick={handleRejectMinor}
              className="w-full bg-white/5 text-white/80 border border-white/20 px-6 py-3 min-h-[48px] rounded-full text-base font-semibold hover:bg-white/10 transition-colors"
            >
              I'm under 18
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
